import { useMemo, useRef, useState } from 'react';
import { Button } from '../ui/Button';
import { HowItWorksLink } from '../ui/HowItWorksLink';
import BookingOrder from './BookingOrder';
import { Icon, IconName } from '../ui/Icon';
import { Checkbox } from '../ui/Checkbox';
import ConfirmDialog from '../ui/ConfirmDialog';
import CreateAssetModal from '../modals/CreateAssetModal';
import CreateTaskModal from '../modals/CreateTaskModal';
import ApplyTemplateButton from './ApplyTemplateButton';
import BulkSlotsBar from './BulkSlotsBar';
import { useAssets, useDeleteAsset, useUpdateAsset } from '../../lib/api/assets';
import { useAuth } from '../../contexts/AuthProvider';
import { useBrands } from '../../lib/api/brands';
import { usePatchTask, useTasks, useTeam } from '../../lib/api/tasks';
import { useTaskActions } from '../tasks/useTaskActions';
import { useToast } from '../ui/Toast';
import {
  AssetCategory,
  ASSET_CATEGORIES,
  AssetStatus,
  Brand,
  Campaign,
  CampaignAsset,
  Task,
  TaskStatus,
  TASK_STATUSES,
} from '../../lib/types';
import { dayDiff, fmtShort, parseDate, today } from '../../lib/dates';
import { CATEGORY_COLOR } from '../../lib/categoryColors';
import { assetDisplayTitle } from '../../lib/assetTitle';
import { ASSET_TYPE_ICON } from '../../lib/assetIcons';
import AssetGanttTimeline from '../gantt/AssetGanttTimeline';
import { openDataUrl } from '../../lib/dataUrl';
import { BrandStack } from '../../lib/brandAvatars';
import { AssetStatusPicker } from './AssetStatusPicker';
import { TaskName } from '../tasks/TaskName';

interface Props {
  campaign: Campaign;
}

/** Tempered tinted background for the category section header — a quieter
 *  cousin of CATEGORY_COLOR so the strip reads as "Email" / "Social
 *  Media" / … without being lively. */
const CATEGORY_HEADER_BG: Record<AssetCategory, string> = {
  Email: 'var(--sitewide-bg)',
  'Social Media': 'var(--brand-bg)',
  Homepage: 'var(--seasonal-bg)',
  Catalog: 'rgba(232,163,62,0.14)',
  Promotion: 'rgba(201,75,154,0.12)',
};

const CATEGORY_ICON: Record<AssetCategory, IconName> = {
  Email: 'mail',
  'Social Media': 'instagram',
  Homepage: 'home',
  Catalog: 'tag',
  Promotion: 'sparkle',
};

const formatDateRange = (a: CampaignAsset): string => {
  if (!a.publish_date) return 'No date — pick one';
  if (a.end_date && a.end_date !== a.publish_date) {
    return `${fmtShort(a.publish_date)} → ${fmtShort(a.end_date)}`;
  }
  if (a.publish_time) return `${fmtShort(a.publish_date)} · ${a.publish_time.slice(0, 5)}`;
  return fmtShort(a.publish_date);
};

/** Where the placement's window sits relative to today. Not a deadline
 *  judgement — "past" just means it has been live and is over. */
const assetTiming = (a: CampaignAsset): 'upcoming' | 'live' | 'past' => {
  if (!a.publish_date) return 'upcoming';
  const t = today();
  const start = parseDate(a.publish_date);
  const end = a.end_date ? parseDate(a.end_date) : start;
  if (end < t) return 'past';
  if (start <= t) return 'live';
  return 'upcoming';
};


const TaskMiniRow = ({
  task,
  team,
  linkedAssetTitle,
  onToggle,
  onChangeStatus,
  onEdit,
  onDelete,
}: {
  task: Task;
  team: { user_id: number; initials: string; color: string }[];
  /** When set, renders a small "for: <asset>" chip on the row so the
   *  context isn't lost when tasks live in the campaign-level section. */
  linkedAssetTitle?: string | null;
  onToggle: () => void;
  onChangeStatus: (next: TaskStatus) => void;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const assignees = (task.assignee_details ?? []).slice(0, 3);
  const done = task.status === 'Done';
  return (
    <div
      className="task-mini-row m-row"
      style={{
        display: 'grid',
        gridTemplateColumns: '4px 24px 1fr 150px 90px 110px 50px 28px 28px 28px 28px',
        gap: 10,
        alignItems: 'center',
        padding: '6px 14px',
      }}
    >
      {task.asset_category ? (
        <div
          style={{
            width: 4, height: 16, borderRadius: 2,
            background: CATEGORY_COLOR[task.asset_category],
            opacity: done ? 0.35 : 0.55,
          }}
        />
      ) : (
        <div />
      )}
      <Checkbox checked={done} onChange={onToggle} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontSize: 12.5,
            color: done ? 'var(--ink-4)' : 'var(--ink-2)',
            textDecoration: done ? 'line-through' : 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          <TaskName name={task.name} />
        </div>
        {linkedAssetTitle && (
          <span
            title={`Linked to: ${linkedAssetTitle}`}
            style={{
              fontSize: 10.5, color: 'var(--ink-4)',
              background: 'var(--bg-subtle)',
              padding: '1px 7px', borderRadius: 'var(--r-pill)',
              maxWidth: 200,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            for: {linkedAssetTitle}
          </span>
        )}
        <div style={{ display: 'flex', gap: 2, marginLeft: 'auto' }}>
          {assignees.map((a) => (
            <span
              key={a.user_id}
              title={a.initials}
              style={{
                width: 18, height: 18, borderRadius: 50,
                background: a.color, color: '#fff',
                fontSize: 9, fontWeight: 600,
                display: 'grid', placeItems: 'center',
              }}
            >
              {a.initials}
            </span>
          ))}
        </div>
      </div>
      <div
        className="mono"
        title={!done && parseDate(task.due_date) < today() ? 'Overdue' : undefined}
        style={{
          fontSize: 11.5,
          color: !done && parseDate(task.due_date) < today() ? 'var(--warn)' : 'var(--ink-4)',
          fontWeight: !done && parseDate(task.due_date) < today() ? 600 : 400,
        }}
      >
        {fmtShort(task.due_date)}
      </div>
      <div />
      <AssetStatusPicker value={task.status} options={TASK_STATUSES} onChange={onChangeStatus} />
      <div />
      <div />
      <div />
      <button
        type="button"
        className="icon-btn"
        onClick={onEdit}
        title="Edit task"
        style={{ width: 24, height: 24 }}
      >
        <Icon name="settings" size={12} />
      </button>
      <button
        type="button"
        className="icon-btn"
        onClick={onDelete}
        title="Delete task"
        style={{ width: 24, height: 24, color: 'var(--ink-4)' }}
      >
        <Icon name="x" size={12} />
      </button>
    </div>
  );
};

const AssetCard = ({
  asset,
  tasks,
  brandsForAsset,
  onEdit,
  onDelete,
  onAddTask,
  onDuplicate,
  onChangeStatus,
  onShowTasks,
  selectMode = false,
  selected = false,
  onToggleSelect,
}: {
  asset: CampaignAsset;
  tasks: Task[];
  brandsForAsset: Brand[];
  onEdit: () => void;
  onDelete: () => void;
  onAddTask: () => void;
  onDuplicate: () => void;
  onChangeStatus: (next: AssetStatus) => void;
  onShowTasks: () => void;
  /** "Brand slots…" mode: clicking the row ticks it instead of opening it. */
  selectMode?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}) => {
  const accent = CATEGORY_COLOR[asset.category];
  return (
    <div
      style={{
        borderTop: '1px solid var(--border-soft)',
        // Bookable placements (brand slots) get a light green tint so they
        // stand out in a long list (Doreen, 2026-10-27).
        background: asset.brand_spots ? '#f4faf6' : 'var(--bg-elevated)',
      }}
    >
      <div
        onClick={selectMode ? onToggleSelect : onEdit}
        className="m-row"
        style={{
          display: 'grid',
          gridTemplateColumns: '4px 24px 1fr 150px 90px 110px 50px 28px 28px 28px 28px',
          background: selected ? '#e3f3e8' : undefined,
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          cursor: 'pointer',
        }}
        title={selectMode ? 'Tick to give it brand slots' : `Edit ${assetDisplayTitle(asset)}`}
      >
        <div style={{ width: 4, height: 22, background: accent, borderRadius: 2 }} />
        {selectMode ? (
          <span onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={selected} onChange={() => onToggleSelect?.()} aria-label="Select placement" />
          </span>
        ) : (
          <Icon name={ASSET_TYPE_ICON[asset.asset_type] ?? CATEGORY_ICON[asset.category]} size={14} />
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 550, letterSpacing: '-0.005em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {assetDisplayTitle(asset)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-4)', display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>{asset.asset_type}</span>
            {asset.brand_spots ? (() => {
              const taken = (asset.brands ?? []).length;
              const over = taken > asset.brand_spots;
              const full = taken === asset.brand_spots;
              return (
                <span
                  title={over ? 'More brands tagged than there are spots' : full ? 'All brand spots taken' : `${asset.brand_spots - taken} brand spot${asset.brand_spots - taken === 1 ? '' : 's'} open`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '1px 8px', borderRadius: 'var(--r-pill)', fontSize: 10.5, fontWeight: 600,
                    background: over ? 'var(--warn-bg)' : full ? 'var(--bg-subtle)' : '#e6f4ea',
                    color: over ? 'var(--warn)' : full ? 'var(--ink-4)' : '#1e7a3c',
                  }}
                >
                  <Icon name="user" size={9} />
                  {taken} / {asset.brand_spots} slot{asset.brand_spots === 1 ? '' : 's'} taken
                  {asset.sold_as_label && asset.sold_as_label.toLowerCase() !== asset.asset_type.toLowerCase() ? ` · ${asset.sold_as_label}` : ''}
                </span>
              );
            })() : null}
            {tasks.length > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onShowTasks();
                }}
                title="Show this placement's tasks below"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: 'var(--bg-subtle)',
                  border: 'none', borderRadius: 'var(--r-pill)',
                  padding: '1px 8px',
                  cursor: 'pointer', fontSize: 10.5, color: 'var(--ink-3)',
                }}
              >
                <Icon name="check" size={9} />
                {tasks.filter((t) => t.status === 'Done').length}/{tasks.length} task{tasks.length === 1 ? '' : 's'}
              </button>
            )}
          </div>
          {(asset.booking_order ?? []).some((o) => o.signed_up_at) && (
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Booking order: <BookingOrder order={asset.booking_order ?? []} />
            </div>
          )}
        </div>
        {(() => {
          const timing = assetTiming(asset);
          return (
            <div
              className="mono"
              title={!asset.publish_date ? 'No date yet — open the placement to pick one' : timing === 'live' ? 'Live now' : timing === 'past' ? 'Was live — period over' : undefined}
              style={{
                fontSize: 11.5,
                display: 'flex', alignItems: 'center', gap: 5,
                // Amber = no date yet (a to-do, same family as the "To plan"
                // tray), blue = planned/upcoming (mosy's "Planned" color),
                // green = live now, faded grey = period over.
                color: !asset.publish_date ? '#a87800'
                  : timing === 'live' ? 'var(--seasonal)'
                  : timing === 'past' ? 'var(--ink-5)'
                  : 'var(--sitewide)',
                fontStyle: !asset.publish_date ? 'italic' : undefined,
              }}
            >
              {!asset.publish_date && <Icon name="calendar" size={11} />}
              {timing === 'live' && (
                <span style={{ width: 6, height: 6, borderRadius: 50, background: 'var(--seasonal)', flexShrink: 0 }} />
              )}
              {formatDateRange(asset)}
            </div>
          );
        })()}
        <div>
          {brandsForAsset.length > 0 && (
            <BrandStack brands={brandsForAsset} max={3} size={18} />
          )}
        </div>
        <AssetStatusPicker value={asset.status} onChange={onChangeStatus} stopParent />
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          {asset.proof_screenshot && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openDataUrl(asset.proof_screenshot);
              }}
              title="Open screenshot"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 22,
                height: 22,
                borderRadius: 50,
                background: 'var(--bg-subtle)',
                color: 'var(--ink-3)',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <Icon name="image" size={13} />
            </button>
          )}
          {asset.proof_url && (
            <a
              href={asset.proof_url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              title="Live URL attached"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 22,
                height: 22,
                borderRadius: 50,
                background: 'var(--bg-subtle)',
                color: 'var(--ink-3)',
                textDecoration: 'none',
              }}
            >
              <Icon name="link" size={13} />
            </a>
          )}
        </div>
        <button
          type="button"
          className="icon-btn"
          onClick={(e) => {
            e.stopPropagation();
            onAddTask();
          }}
          title="Add a task linked to this placement"
        >
          <Icon name="plus" size={12} />
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
          title="Duplicate this placement"
        >
          <Icon name="copy" size={12} />
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          title="Edit placement"
        >
          <Icon name="settings" size={12} />
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Delete placement"
          style={{ color: 'var(--ink-4)' }}
        >
          <Icon name="x" size={12} />
        </button>
      </div>
      {/* Tasks moved to the campaign-level "Tasks" section. The chip in
          the subtitle above shows the count and filters that section. */}
    </div>
  );
};

export const AssetsSection = ({ campaign }: Props) => {
  const campaignId = campaign.id;
  const { data: assets = [], isLoading } = useAssets({ campaign: campaignId });
  const { data: tasks = [] } = useTasks({ campaign: campaignId });
  const { data: team = [] } = useTeam();
  const { data: brands = [] } = useBrands();
  const brandById = useMemo(() => new Map(brands.map((b) => [b.id, b])), [brands]);
  const patchTask = usePatchTask();
  const taskActions = useTaskActions();
  const deleteAsset = useDeleteAsset();
  const updateAsset = useUpdateAsset();
  const toast = useToast();

  const { user: currentUser } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  // When set, the Tasks section narrows to that asset's tasks. Clicking
  // the count chip on an asset row sets this + scrolls the section in.
  const [taskFilterAssetId, setTaskFilterAssetId] = useState<number | null>(null);
  const [tasksMineOnly, setTasksMineOnly] = useState(false);
  const tasksSectionRef = useRef<HTMLDivElement>(null);
  const [addTaskTarget, setAddTaskTarget] = useState<CampaignAsset | null>(null);
  const [editTarget, setEditTarget] = useState<CampaignAsset | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<CampaignAsset | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CampaignAsset | null>(null);

  const tasksByAsset = useMemo(() => {
    const map = new Map<number, Task[]>();
    for (const t of tasks) {
      if (t.asset == null) continue;
      const arr = map.get(t.asset) ?? [];
      arr.push(t);
      map.set(t.asset, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.due_date.localeCompare(b.due_date));
    }
    return map;
  }, [tasks]);

  const assetById = useMemo(
    () => new Map(assets.map((a) => [a.id, a])),
    [assets],
  );

  // ALL tasks live in the bottom "Tasks" section now (no longer nested
  // under each asset). Linked tasks show a small "for: <asset>" chip so
  // the context isn't lost; the asset row shows a clickable task-count
  // chip that filters this section to that asset.
  const allTasksSorted = useMemo(
    () => [...tasks].sort((a, b) => a.due_date.localeCompare(b.due_date)),
    [tasks],
  );
  const visibleTasks = useMemo(() => {
    let list = allTasksSorted;
    if (taskFilterAssetId != null) {
      list = list.filter((t) => t.asset === taskFilterAssetId);
    }
    if (tasksMineOnly && currentUser) {
      list = list.filter((t) => t.assignees.includes(currentUser.user_id));
    }
    return list;
  }, [allTasksSorted, taskFilterAssetId, tasksMineOnly, currentUser]);

  const mineCount = useMemo(() => (currentUser
    ? allTasksSorted.filter((t) => t.assignees.includes(currentUser.user_id)).length
    : 0), [allTasksSorted, currentUser]);

  const showTasksForAsset = (assetId: number) => {
    setTaskFilterAssetId(assetId);
    requestAnimationFrame(() => {
      tasksSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const filteredAsset = taskFilterAssetId != null ? assetById.get(taskFilterAssetId) : null;

  // Brand filter for multi-brand campaigns (e.g. Black Friday): show only
  // the placements directly tagged with one brand — same strict rule as
  // the brand pages. null = all brands.
  const [brandFilter, setBrandFilter] = useState<string | null>(null);
  const campaignBrands = useMemo(
    () =>
      (campaign.brands ?? [])
        .map((bid) => brandById.get(bid))
        .filter((b): b is Brand => !!b)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [campaign.brands, brandById],
  );
  // "Open spots": only sellable placements (brand_spots set) that still
  // have room — taken = brands tagged on the placement.
  const [openSpotsOnly, setOpenSpotsOnly] = useState(false);
  // "Brand slots…": tick placements, then set their slots in one go.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const toggleSelect = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const openSpots = (a: CampaignAsset) =>
    a.brand_spots ? Math.max(0, a.brand_spots - (a.brands ?? []).length) : 0;
  const hasSpots = useMemo(() => assets.some((a) => !!a.brand_spots), [assets]);
  const totalOpenSpots = useMemo(
    () => assets.reduce((n, a) => n + openSpots(a), 0),
    [assets],
  );
  const visibleAssets = useMemo(
    () => assets
      .filter((a) => !brandFilter || (a.brands ?? []).includes(brandFilter))
      .filter((a) => !openSpotsOnly || openSpots(a) > 0),
    [assets, brandFilter, openSpotsOnly],
  );

  // Undated assets travel together in a "To plan" tray above the
  // categorised list; dated ones stay in their category groups.
  const unscheduledAssets = useMemo(
    () => visibleAssets.filter((a) => !a.publish_date),
    [visibleAssets],
  );
  const groupedAssets = useMemo(() => {
    const groups = new Map<AssetCategory, CampaignAsset[]>();
    for (const a of visibleAssets) {
      if (!a.publish_date) continue;
      const arr = groups.get(a.category) ?? [];
      arr.push(a);
      groups.set(a.category, arr);
    }
    for (const arr of groups.values()) {
      arr.sort((a, b) => (a.publish_date ?? '').localeCompare(b.publish_date ?? ''));
    }
    return groups;
  }, [visibleAssets]);

  // List (grouped by category) or a simple timeline over the campaign
  // period — one bar per placement, colored by category.
  const [view, setView] = useState<'list' | 'timeline'>('list');

  /** Window for the timeline: from just before the campaign starts to just
   *  after it ends (the Gantt component anchors on today, so convert). */
  const timelineRange = useMemo(() => {
    const t = today();
    const s = campaign.start_date ? parseDate(campaign.start_date) : t;
    const e = campaign.end_date ? parseDate(campaign.end_date) : s;
    const startOffsetWeeks = Math.floor(dayDiff(t, s) / 7) - 1;
    const rangeWeeks = Math.max(4, Math.ceil(dayDiff(s, e) / 7) + 3);
    return { startOffsetWeeks, rangeWeeks };
  }, [campaign.start_date, campaign.end_date]);

  const onToggleTask = (t: Task) =>
    patchTask.mutate({ id: t.id, patch: { status: t.status === 'Done' ? 'In progress' : 'Done' } });

  const onChangeTaskStatus = (t: Task, next: TaskStatus) => {
    patchTask.mutate(
      { id: t.id, patch: { status: next } },
      {
        onSuccess: () => toast.push(`Status set to "${next}"`),
        onError: () => toast.push('Could not update status'),
      },
    );
  };

  const onChangeAssetStatus = (asset: CampaignAsset, next: AssetStatus) => {
    updateAsset.mutate(
      { id: asset.id, patch: { status: next } },
      {
        onSuccess: () => toast.push(`Status set to "${next}"`),
        onError: () => toast.push('Could not update status'),
      },
    );
  };

  const handleDeleteAsset = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAsset.mutateAsync(deleteTarget.id);
      toast.push('Placement deleted');
    } catch {
      toast.push('Could not delete placement');
    }
    setDeleteTarget(null);
  };

  return (
    <>
      <div className="section">
        <div className="section-head">
          <div className="section-title">Campaign placements</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              className={`filter-pill ${view === 'list' ? 'active' : ''}`}
              onClick={() => setView('list')}
            >
              <Icon name="task" size={12} /> List
            </button>
            <button
              type="button"
              className={`filter-pill ${view === 'timeline' ? 'active' : ''}`}
              onClick={() => setView('timeline')}
            >
              <Icon name="trending" size={12} /> Timeline
            </button>
            <div style={{ width: 1, height: 20, background: 'var(--border)' }} />
            <HowItWorksLink section="placements" />
            <Button
              variant="ghost"
              onClick={() => { setSelectMode((v) => !v); setSelectedIds(new Set()); setView('list'); }}
              title="Tick several placements and give them brand slots in one go"
            >
              <Icon name="user" size={13} /> {selectMode ? 'Stop selecting' : 'Brand slots…'}
            </Button>
            <ApplyTemplateButton campaign={campaign} />
            <Button variant="ghost" onClick={() => setCreateOpen(true)}>
              <Icon name="plus" size={13} /> New placement
            </Button>
          </div>
        </div>

        {(campaignBrands.length >= 2 || hasSpots) && assets.length > 0 && (
          <div className="filter-bar" style={{ marginBottom: 8 }}>
            {hasSpots && (
              <button
                type="button"
                className={`filter-pill ${openSpotsOnly ? 'active' : ''}`}
                onClick={() => setOpenSpotsOnly((v) => !v)}
                title="Show only placements where brands can still book a spot"
              >
                Open spots · {totalOpenSpots}
              </button>
            )}
            {campaignBrands.length >= 2 && (<>
            <button
              type="button"
              className={`filter-pill ${brandFilter === null ? 'active' : ''}`}
              onClick={() => setBrandFilter(null)}
            >
              All brands
            </button>
            {campaignBrands.map((b) => (
              <button
                key={b.id}
                type="button"
                className={`filter-pill ${brandFilter === b.id ? 'active' : ''}`}
                onClick={() => setBrandFilter((prev) => (prev === b.id ? null : b.id))}
                title={`Show only placements tagged with ${b.name}`}
              >
                <span className="dot" style={{ background: b.color }} />
                {b.name}
              </button>
            ))}
            </>)}
          </div>
        )}

        {selectMode && assets.length > 0 && (
          <div className="filter-bar" style={{ marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--ink-4)', marginRight: 2 }}>Select all:</span>
            {[...new Set(visibleAssets.map((a) => a.asset_type))].map((t) => {
              const ofType = visibleAssets.filter((a) => a.asset_type === t);
              const all = ofType.every((a) => selectedIds.has(a.id));
              return (
                <button
                  key={t}
                  type="button"
                  className={`filter-pill ${all ? 'active' : ''}`}
                  onClick={() => setSelectedIds((prev) => {
                    const next = new Set(prev);
                    ofType.forEach((a) => (all ? next.delete(a.id) : next.add(a.id)));
                    return next;
                  })}
                >
                  {t} · {ofType.length}
                </button>
              );
            })}
          </div>
        )}

        {isLoading ? (
          <div className="card" style={{ padding: 20, color: 'var(--ink-4)' }}>Loading…</div>
        ) : assets.length === 0 ? (
          <div className="card" style={{ padding: 24, textAlign: 'center' }}>
            <p style={{ color: 'var(--ink-3)', marginBottom: 12 }}>
              No placements yet. Add what's going to be published as part of this campaign — newsletters, posts, banners…
            </p>
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              <Icon name="plus" size={13} /> New placement
            </Button>
          </div>
        ) : visibleAssets.length === 0 ? (
          <div className="card" style={{ padding: 20, color: 'var(--ink-3)', fontSize: 13 }}>
            {brandFilter ? (
              <>
                No placements are tagged with {brandById.get(brandFilter)?.name ?? 'this brand'}
                {openSpotsOnly ? ' that still have open spots' : ''} yet.
                Tag a placement with the brand (open it and tick the brand) to see it here.
              </>
            ) : (
              <>All brand spots are taken — no placement has an open spot left.</>
            )}
          </div>
        ) : view === 'timeline' ? (
          <>
            {unscheduledAssets.length > 0 && (
              <div
                className="card"
                style={{
                  padding: '8px 14px', marginBottom: 10, fontSize: 12,
                  background: '#fff7e0', color: '#7a5300',
                }}
              >
                {unscheduledAssets.length} placement{unscheduledAssets.length > 1 ? 's' : ''} without
                a date {unscheduledAssets.length > 1 ? "don't" : "doesn't"} show on the timeline —
                switch to List to pick a date.
              </div>
            )}
            <AssetGanttTimeline
              assets={visibleAssets}
              onAssetClick={(a) => setEditTarget(a)}
              colorFor={(a) => CATEGORY_COLOR[a.category] || 'var(--ink-3)'}
              labelHeader="Placement"
              rangeWeeks={timelineRange.rangeWeeks}
              startOffsetWeeks={timelineRange.startOffsetWeeks}
              fitWidth
            />
          </>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {unscheduledAssets.length > 0 && (
              <div>
                <div
                  style={{
                    padding: '10px 14px',
                    background: '#fff7e0',
                    borderTop: '1px solid var(--border)',
                    borderBottom: '1px solid var(--border)',
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    fontWeight: 600,
                    color: '#7a5300',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <Icon name="calendar" size={11} />
                  To plan
                  <span style={{ color: '#a87800', fontWeight: 500 }}>
                    · {unscheduledAssets.length}
                  </span>
                  <span style={{ marginLeft: 'auto', textTransform: 'none', letterSpacing: 0, fontWeight: 400, fontSize: 11, color: '#a87800' }}>
                    Pick a date on each to schedule it
                  </span>
                </div>
                {unscheduledAssets.map((asset) => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    tasks={tasksByAsset.get(asset.id) ?? []}
                    brandsForAsset={
                      ((asset.brands as string[]) ?? [])
                        .map((bid) => brandById.get(bid))
                        .filter((b): b is Brand => !!b)
                    }
                    onEdit={() => setEditTarget(asset)}
                    onDelete={() => setDeleteTarget(asset)}
                    onAddTask={() => setAddTaskTarget(asset)}
                    onDuplicate={() => setDuplicateTarget(asset)}
                    onChangeStatus={(next) => onChangeAssetStatus(asset, next)}
                    onShowTasks={() => showTasksForAsset(asset.id)}
                    selectMode={selectMode}
                    selected={selectedIds.has(asset.id)}
                    onToggleSelect={() => toggleSelect(asset.id)}
                  />
                ))}
              </div>
            )}
            {ASSET_CATEGORIES.filter((c) => groupedAssets.has(c)).map((cat) => (
              <div key={cat}>
                <div
                  style={{
                    padding: '10px 14px',
                    background: CATEGORY_HEADER_BG[cat],
                    borderTop: '1px solid var(--border)',
                    borderBottom: '1px solid var(--border)',
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    fontWeight: 600,
                    color: 'var(--ink-3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span
                    className="dot"
                    style={{ background: CATEGORY_COLOR[cat], width: 6, height: 6, borderRadius: 50 }}
                  />
                  {cat}
                  <span style={{ color: 'var(--ink-5)', fontWeight: 500 }}>
                    · {groupedAssets.get(cat)!.length}
                  </span>
                </div>
                {groupedAssets.get(cat)!.map((asset) => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    tasks={tasksByAsset.get(asset.id) ?? []}
                    brandsForAsset={
                      ((asset.brands as string[]) ?? [])
                        .map((bid) => brandById.get(bid))
                        .filter((b): b is Brand => !!b)
                    }
                    onEdit={() => setEditTarget(asset)}
                    onDelete={() => setDeleteTarget(asset)}
                    onAddTask={() => setAddTaskTarget(asset)}
                    onDuplicate={() => setDuplicateTarget(asset)}
                    onChangeStatus={(next) => onChangeAssetStatus(asset, next)}
                    onShowTasks={() => showTasksForAsset(asset.id)}
                    selectMode={selectMode}
                    selected={selectedIds.has(asset.id)}
                    onToggleSelect={() => toggleSelect(asset.id)}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {selectMode && (
        <BulkSlotsBar
          selected={assets.filter((a) => selectedIds.has(a.id))}
          onClear={() => setSelectedIds(new Set())}
          onDone={() => { setSelectMode(false); setSelectedIds(new Set()); }}
        />
      )}

      <div className="section" ref={tasksSectionRef}>
        <div className="section-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div className="section-title">Tasks</div>
            {filteredAsset && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 11.5, color: 'var(--ink-3)',
                background: 'var(--bg-subtle)',
                padding: '3px 10px', borderRadius: 'var(--r-pill)',
              }}>
                <span>For: {filteredAsset.title}</span>
                <button
                  type="button"
                  onClick={() => setTaskFilterAssetId(null)}
                  title="Clear filter"
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: 'var(--ink-4)', padding: 0, lineHeight: 1, fontSize: 14,
                  }}
                >
                  ×
                </button>
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {currentUser && (
              <button
                type="button"
                onClick={() => setTasksMineOnly((v) => !v)}
                className={`filter-pill ${tasksMineOnly ? 'active' : ''}`}
                title={tasksMineOnly ? 'Show all tasks' : 'Show only tasks assigned to me'}
              >
                <Icon name="user" size={11} />
                {tasksMineOnly ? 'Only mine' : 'Only mine'}
                {mineCount > 0 && (
                  <span style={{
                    marginLeft: 4, fontSize: 10, opacity: 0.7,
                  }}>
                    · {mineCount}
                  </span>
                )}
              </button>
            )}
            <Button variant="ghost" onClick={() => setCreateTaskOpen(true)}>
              <Icon name="plus" size={13} /> New task
            </Button>
          </div>
        </div>
        {visibleTasks.length === 0 ? (
          <div className="card subdued" style={{ padding: 18, color: 'var(--ink-4)', textAlign: 'center' }}>
            {tasksMineOnly && filteredAsset
              ? 'No tasks assigned to you on this placement.'
              : tasksMineOnly
              ? 'No tasks assigned to you on this campaign.'
              : filteredAsset
              ? 'No tasks for this placement yet.'
              : 'No tasks yet.'}
          </div>
        ) : (
          <div className="card subdued" style={{ padding: 0, overflow: 'hidden' }}>
            {visibleTasks.map((t) => {
              const linkedAsset = t.asset != null ? assetById.get(t.asset) : null;
              return (
                <TaskMiniRow
                  key={t.id}
                  task={t}
                  team={team}
                  linkedAssetTitle={(() => {
                    if (!linkedAsset) return null;
                    const display = assetDisplayTitle(linkedAsset);
                    // Skip the "for:" chip when the task name already names
                    // the placement — no point saying it twice on one row.
                    return t.name.toLowerCase().includes(display.toLowerCase()) ? null : display;
                  })()}
                  onToggle={() => onToggleTask(t)}
                  onChangeStatus={(next) => onChangeTaskStatus(t, next)}
                  onEdit={() => taskActions.openEdit(t)}
                  onDelete={() => taskActions.openDelete(t)}
                />
              );
            })}
          </div>
        )}
      </div>

      <CreateAssetModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultCampaignId={campaignId}
      />
      <CreateAssetModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        asset={editTarget}
      />
      <CreateAssetModal
        open={!!duplicateTarget}
        onClose={() => setDuplicateTarget(null)}
        duplicateOf={duplicateTarget}
        defaultCampaignId={campaignId}
      />
      <CreateTaskModal
        open={createTaskOpen}
        onClose={() => setCreateTaskOpen(false)}
        campaignId={campaignId}
      />
      <CreateTaskModal
        open={!!addTaskTarget}
        onClose={() => setAddTaskTarget(null)}
        campaignId={campaignId}
        assetId={addTaskTarget?.id}
        assetTitle={addTaskTarget?.title}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteAsset}
        title={deleteTarget ? `Delete "${assetDisplayTitle(deleteTarget)}"?` : ''}
        description="This will remove the placement from this campaign. Tasks linked to it stay (just unlinked)."
        confirmLabel="Delete placement"
        danger
        busy={deleteAsset.isPending}
      />
      {taskActions.modals}
    </>
  );
};

export default AssetsSection;
